package fr.frux.feature.map

import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.Close
import androidx.compose.material.icons.outlined.Eco
import androidx.compose.material.icons.outlined.Landscape
import androidx.compose.material.icons.outlined.LocationCity
import androidx.compose.material.icons.outlined.Park
import androidx.compose.material.icons.outlined.Place
import androidx.compose.material.icons.outlined.Search
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.shadow
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.platform.LocalFocusManager
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.semantics.heading
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import fr.frux.core.designsystem.components.FruxCard
import fr.frux.core.designsystem.components.FruxIconButton
import fr.frux.core.designsystem.theme.FruxSpacing
import fr.frux.feature.map.resources.Res
import fr.frux.feature.map.resources.map_search_clear_description
import fr.frux.feature.map.resources.map_search_placeholder
import fr.frux.feature.map.resources.map_search_places_header
import fr.frux.feature.map.resources.map_search_species_header
import fr.frux.feature.map.resources.map_search_trees_header
import fr.frux.shared.domain.model.Place
import fr.frux.shared.domain.model.PlaceType
import fr.frux.shared.domain.model.Species
import fr.frux.shared.domain.model.Tree
import org.jetbrains.compose.resources.stringResource

@Composable
internal fun SearchPill(
    query: String,
    onQueryChange: (String) -> Unit,
    modifier: Modifier = Modifier,
) {
    val focusManager = LocalFocusManager.current
    // Invite du champ de recherche, et son libelle d'accessibilite — un seul mot pour les deux.
    val searchFieldLabel = stringResource(Res.string.map_search_placeholder)
    Surface(
        modifier =
            modifier
                .shadow(2.dp, RoundedCornerShape(50)),
        shape = RoundedCornerShape(50),
        color = MaterialTheme.colorScheme.surface,
    ) {
        Row(
            modifier = Modifier.padding(horizontal = FruxSpacing.md, vertical = FruxSpacing.sm),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Icon(
                imageVector = Icons.Outlined.Search,
                contentDescription = null,
                tint = MaterialTheme.colorScheme.onSurfaceVariant,
            )
            Spacer(modifier = Modifier.width(FruxSpacing.sm))
            Box(modifier = Modifier.weight(1f)) {
                if (query.isEmpty()) {
                    Text(
                        text = searchFieldLabel,
                        style = MaterialTheme.typography.bodyLarge,
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                    )
                }
                BasicTextField(
                    value = query,
                    onValueChange = onQueryChange,
                    singleLine = true,
                    textStyle =
                        MaterialTheme.typography.bodyLarge.copy(
                            color = MaterialTheme.colorScheme.onSurface,
                        ),
                    cursorBrush =
                        androidx.compose.ui.graphics
                            .SolidColor(MaterialTheme.colorScheme.primary),
                    // Relevé par #126 : aucun imeAction jusqu'ici, la touche Entree du clavier
                    // n'avait aucun effet. Search plutot que Done : le champ pilote une recherche
                    // en direct (debounce cote VM), pas un formulaire a valider.
                    keyboardOptions = KeyboardOptions(imeAction = ImeAction.Search),
                    keyboardActions = KeyboardActions(onSearch = { focusManager.clearFocus() }),
                    // Le texte d'invite est un Text frere, pas le libelle du champ : sans ceci,
                    // le champ arrive vide dans l'arbre d'accessibilite et TalkBack n'annonce
                    // rien en le focalisant (verifie sur emulateur, #57).
                    modifier =
                        Modifier
                            .fillMaxWidth()
                            .semantics { contentDescription = searchFieldLabel },
                )
            }
            // Remplace une pastille d'avatar jamais câblée (28 dp, sans icône, sans onClick ni
            // libellé) : une cible sous les 48 dp requis qui n'allait nulle part. L'effacement est
            // l'action attendue à cette place, et n'a besoin d'aucune destination.
            if (query.isNotEmpty()) {
                FruxIconButton(
                    icon = Icons.Outlined.Close,
                    contentDescription = stringResource(Res.string.map_search_clear_description),
                    onClick = { onQueryChange("") },
                    tint = MaterialTheme.colorScheme.onSurfaceVariant,
                )
            }
        }
    }
}

/**
 * Une seule carte de resultats sous la pill (#394, revue en sections unique) : especes
 * suggerees, arbres visibles et lieux geocodes, chacun dans sa propre section dont l'ordre suit
 * [placesFirst]. Chaque section n'apparait que si elle a quelque chose a montrer, et son titre
 * avec elle. La colonne defile a la verticale au-dela d'une hauteur bornee, pour ne rien pousser
 * hors de l'ecran quand le clavier est ouvert.
 */
@Composable
internal fun SearchResultsList(
    species: List<Species>,
    trees: List<Tree>,
    places: List<Place>,
    placesFirst: Boolean,
    onSpeciesClick: (Species) -> Unit,
    onTreeClick: (Tree) -> Unit,
    onPlaceClick: (Place) -> Unit,
    modifier: Modifier = Modifier,
) {
    FruxCard(modifier = modifier) {
        Column(
            modifier =
                Modifier
                    .heightIn(max = 360.dp)
                    .verticalScroll(rememberScrollState()),
        ) {
            val speciesSection: @Composable () -> Unit = {
                if (species.isNotEmpty()) {
                    SectionHeader(stringResource(Res.string.map_search_species_header))
                    species.forEach { suggestion ->
                        ResultRow(
                            icon = leafIcon(),
                            title = suggestion.commonNameFr,
                            subtitle = suggestion.scientificName,
                            onClick = { onSpeciesClick(suggestion) },
                        )
                    }
                }
            }
            val treesSection: @Composable () -> Unit = {
                if (trees.isNotEmpty()) {
                    SectionHeader(stringResource(Res.string.map_search_trees_header))
                    trees.take(MAX_TREE_RESULTS).forEach { tree ->
                        ResultRow(
                            icon = Icons.Outlined.Park,
                            title = tree.species.commonNameFr,
                            subtitle = null,
                            onClick = { onTreeClick(tree) },
                        )
                    }
                }
            }
            val placesSection: @Composable () -> Unit = {
                if (places.isNotEmpty()) {
                    SectionHeader(stringResource(Res.string.map_search_places_header))
                    places.forEach { place ->
                        ResultRow(
                            icon = place.type.icon(),
                            title = place.label,
                            subtitle = place.detail,
                            onClick = { onPlaceClick(place) },
                        )
                    }
                }
            }
            if (placesFirst) {
                placesSection()
                speciesSection()
                treesSection()
            } else {
                speciesSection()
                treesSection()
                placesSection()
            }
        }
    }
}

private const val MAX_TREE_RESULTS = 5

@Composable
private fun SectionHeader(text: String) {
    Text(
        text = text,
        style = MaterialTheme.typography.labelMedium,
        color = MaterialTheme.colorScheme.onSurfaceVariant,
        modifier =
            Modifier
                .padding(top = FruxSpacing.xs, bottom = FruxSpacing.xs)
                .semantics { heading() },
    )
}

/** Icone feuille pour une suggestion d'espece : jamais un emoji (carre vide sur le web). */
private fun leafIcon(): ImageVector = Icons.Outlined.Eco

@Composable
private fun ResultRow(
    icon: ImageVector,
    title: String,
    subtitle: String?,
    onClick: () -> Unit,
) {
    Row(
        modifier =
            Modifier
                .fillMaxWidth()
                .heightIn(min = 48.dp)
                .clickable(onClick = onClick)
                .padding(vertical = FruxSpacing.xs),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Icon(imageVector = icon, contentDescription = null, tint = MaterialTheme.colorScheme.primary)
        Spacer(modifier = Modifier.width(FruxSpacing.sm))
        Column(modifier = Modifier.weight(1f)) {
            Text(
                text = title,
                style = MaterialTheme.typography.bodyLarge,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
            )
            if (subtitle != null) {
                Text(
                    text = subtitle,
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                )
            }
        }
    }
}

/** Icones Outlined seulement : un emoji dans un Text est un carre vide sur le web. */
private fun PlaceType.icon(): ImageVector =
    when (this) {
        PlaceType.MUNICIPALITY -> Icons.Outlined.LocationCity
        PlaceType.LOCALITY -> Icons.Outlined.Landscape
        PlaceType.STREET, PlaceType.ADDRESS -> Icons.Outlined.Place
        PlaceType.POI -> Icons.Outlined.Park
    }
